// Copyright (C) 2020-2021 Intel
// SPDX-License-Identifier: MIT

import React from 'react';
import { connect } from 'react-redux';

import { CombinedState, ShapeType, ObjectType, ActiveControl } from 'reducers/interfaces';
import { rememberObject, updateAnnotationsAsync } from 'actions/annotation-actions';

import { ObservedQuickPolygonLabelControl as PresentationalObservedQuickPolygonLabelControl } from 'components/annotation-page/standard-workspace/controls-side-bar/quick-polygon-label-control';

interface OwnProps {
    // forwarded props from the sidebar
    canvasInstance: any;
    activeControl: ActiveControl;
    disabled?: boolean;
    labels: any[];
    labelName: string;
    iconComponent: React.ComponentType;
    shortcutHint?: string;
}

interface StateToProps {
    activeLabelID?: number;
    states: any[];
    labels: any[];
}

interface DispatchToProps {
    onStarted(labelId: number): void;
    onStopped(): void;
    updateAnnotations(states: any[]): void;
}

export type Props = OwnProps & StateToProps & DispatchToProps;

function QuickPolygonLabelControl(props: Props): JSX.Element {
    const { canvasInstance, states, labels, updateAnnotations, ...rest } = props;

    const onRelabelSelected = (labelId: number): boolean => {
        // read highlighted map from canvas
        const highlighted = (canvasInstance as any)?.view?.groupHandler?.highlightedShapes || {};
        const highlightedIDs = new Set(Object.keys(highlighted).map((k) => Number(k)));

        if (!highlightedIDs.size) return false;

        // resolve label object once
        const newLabel = labels.find((l: any) => l.id === labelId);
        if (!newLabel) return false;

        // collect and mutate selected states
        const toUpdate: any[] = [];
        for (const st of states) {
            if (highlightedIDs.has(st.clientID)) {
                st.label = newLabel;
                toUpdate.push(st);
            }
        }
        if (!toUpdate.length) return false;

        updateAnnotations(toUpdate);
        return true;
    };

    return (
        <PresentationalObservedQuickPolygonLabelControl
            {...rest}
            labels={labels}
            canvasInstance={canvasInstance}
            onRelabelSelected={onRelabelSelected}
        />
    );
}

function mapStateToProps(state: CombinedState): StateToProps {
    const activeLabelID = (state as any)?.annotation?.drawing?.activeLabelID;
    const states = (state as any)?.annotation?.annotations.states;
    const labels = (state as any)?.annotation?.job.labels;
    return { activeLabelID, states, labels };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        onStarted(labelId: number): void {
            dispatch(
                rememberObject({
                    activeObjectType: ObjectType.SHAPE,
                    activeShapeType: ShapeType.POLYGON,
                    activeLabelID: labelId,
                    activeNumOfPoints: undefined,
                    activeRectDrawingMethod: undefined,
                    activeCuboidDrawingMethod: undefined,
                }),
            );
        },
        onStopped(): void {
            // nothing to persist when stopping; keep last-used settings
        },
        updateAnnotations(states: any[]): void {
            dispatch(updateAnnotationsAsync(states));
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(QuickPolygonLabelControl));
